import type { OnDestroy, Type } from '@angular/core';
import {
  EnvironmentInjector,
  Injectable,
  ViewContainerRef,
  createComponent,
  inject,
} from '@angular/core';

import type { Observable } from 'rxjs';
import { merge, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import type { CreateNodeView, RtPMPlugin } from '@core/prosemirror-legacy';

export interface ProsemirrorAngularComponent {
  pmOnDestroy$: Subject<void>;
  getNodeView: CreateNodeView;
}

@Injectable()
export class ProsemirrorBaseConfig implements OnDestroy {
  protected readonly environmentInjector = inject(EnvironmentInjector);
  protected readonly vcRef = inject(ViewContainerRef);

  public eventStream$: Subject<any>;
  protected ngOnDestroy$ = new Subject<void>();

  public listenForPluginEvents(plugins: RtPMPlugin[]) {
    const streams = <Array<Observable<any>>>(
      plugins.map(({ eventStream$ }) => eventStream$).filter(Boolean)
    );

    merge(...streams)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(event => this.eventStream$.next(event));
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  protected prosemirrorComponentFactory(component: Type<ProsemirrorAngularComponent>) {
    return (...args: Parameters<CreateNodeView>) => {
      const componentRef = createComponent(component, {
        environmentInjector: this.environmentInjector,
        elementInjector: this.vcRef.injector,
      });
      componentRef.changeDetectorRef.detectChanges();

      componentRef.instance.pmOnDestroy$.pipe(take(1)).subscribe(() => componentRef.destroy());
      return componentRef.instance.getNodeView(...args);
    };
  }
}
